import React, { useState } from "react";
import { useSelector } from "react-redux";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";

const BillingHistory = () => {
  const { sale } = useSelector((state) => state.sale);

  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all"); // cash, online, all
  const [sortOrder, setSortOrder] = useState("desc"); // desc or asc
  const [productFilter, setProductFilter] = useState(""); // ✅ new filter

  // Format date nicely
  const formatDate = (dateString) =>
    new Date(dateString).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

  // Filter and sort the sales array
  const filteredTransactions = sale
    ?.filter((tx) => {
      const matchesSearch =
        tx?.employeeId?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tx?._id?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus =
        statusFilter === "all" || tx.paymentMethod === statusFilter;

      const matchesProduct =
        !productFilter ||
        tx.services?.some((s) =>
          s?.name?.toLowerCase().includes(productFilter.toLowerCase())
        );

      const matchesDate =
        dateFilter === "all" ||
        (() => {
          const transactionDate = new Date(tx.createdAt);
          const now = new Date();

          const tYear = transactionDate.getFullYear();
          const tMonth = transactionDate.getMonth();
          const tDate = transactionDate.getDate();

          const nYear = now.getFullYear();
          const nMonth = now.getMonth();
          const nDate = now.getDate();

          switch (dateFilter) {
            case "today":
              return tYear === nYear && tMonth === nMonth && tDate === nDate;

            case "week":
              const startOfWeek = new Date(now);
              startOfWeek.setHours(0, 0, 0, 0);
              startOfWeek.setDate(now.getDate() - now.getDay()); // Sunday
              return transactionDate >= startOfWeek && transactionDate <= now;

            case "month":
              return tYear === nYear && tMonth === nMonth;

            default:
              return true;
          }
        })();

      return matchesSearch && matchesStatus && matchesDate && matchesProduct;
    })
    ?.sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });

  // Calculate total amount from filtered
  const getTotalAmount = () =>
    filteredTransactions.reduce((sum, t) => sum + (t.amount || 0), 0);

  // Generate PDF report function
  const generatePDFReport = () => {
    if (!filteredTransactions || filteredTransactions.length === 0) {
      alert("No data to generate report");
      return;
    }

    const doc = new jsPDF();

    doc.setFontSize(18);
    doc.text("Billing Transactions Report", 14, 22);

    doc.setFontSize(11);
    doc.setTextColor(100);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 30);

    const headers = [
      [
        "Transaction ID",
        "Employee",
        "Products",
        "Amount",
        "Date",
        "Payment Method",
      ],
    ];
    const rows = filteredTransactions?.map((tx) => [
      tx._id,
      tx.employeeId?.name || "",
      tx.services
        ?.map((s) => `${s?.name} (Qty: ${s?.quantity})`)
        .join(", ") || "",
      `$${tx?.amount?.toFixed(2)}`,
      formatDate(tx?.createdAt),
      tx.paymentMethod,
    ]);

    autoTable(doc, {
      head: headers,
      body: rows,
      startY: 35,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [41, 128, 185] },
      alternateRowStyles: { fillColor: [240, 240, 240] },
    });

    doc.save(`billing_report_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  return (
    <div className="space-y-6">
      {/* Filters and Search */}
      <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-4 flex-1">
          <div className="flex-1 max-w-md">
            <Input
              type="search"
              placeholder="Search by employee or transaction..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full"
            />
          </div>

          {/* ✅ Product filter */}
          <Input
            type="search"
            placeholder="Filter by product..."
            value={productFilter}
            onChange={(e) => setProductFilter(e.target.value)}
            className="w-full sm:w-48"
          />

          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="px-3 py-2 border border-border mr-2 rounded-md text-sm bg-background"
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-border rounded-md text-sm bg-background"
          >
            <option value="all">All Payment Methods</option>
            <option value="cash">Cash</option>
            <option value="online">Online</option>
          </select>

          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            className="px-3 py-2 border border-border rounded-md text-sm bg-background"
          >
            <option value="desc">Newest First</option>
            <option value="asc">Oldest First</option>
          </select>
        </div>

        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            iconName="FileText"
            iconPosition="left"
            iconSize={16}
            onClick={generatePDFReport}
          >
            Generate Report
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="DollarSign" size={16} className="text-success" />
            <span className="text-sm text-muted-foreground">Total Amount</span>
          </div>
          <p className="text-xl font-bold text-foreground">
            ${getTotalAmount()?.toFixed(2)}
          </p>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Receipt" size={16} className="text-primary" />
            <span className="text-sm text-muted-foreground">
              Total Transactions
            </span>
          </div>
          <p className="text-xl font-bold text-foreground">
            {filteredTransactions?.length}
          </p>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="DollarSign" size={16} className="text-success" />
            <span className="text-sm text-muted-foreground">Cash</span>
          </div>
          <p className="text-xl font-bold text-foreground">
            {filteredTransactions.filter((t) => t.paymentMethod === "cash")
              .length}
          </p>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="CreditCard" size={16} className="text-primary" />
            <span className="text-sm text-muted-foreground">Online</span>
          </div>
          <p className="text-xl font-bold text-foreground">
            {filteredTransactions.filter((t) => t.paymentMethod === "online")
              .length}
          </p>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block">
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    #
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Employee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Products
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Payment Method
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredTransactions.map((tx, index) => (
                  <tr key={tx._id} className="hover:bg-muted/25 micro-animation">
                    <td className="px-6 py-4 font-mono text-sm">{index + 1}</td>
                    <td className="px-6 py-4">{tx.employeeId?.name}</td>
                    <td className="px-6 py-4 text-sm">
                      {tx.services
                        ?.map((s) => `${s.name} (Qty: ${s.quantity})`)
                        .join(", ")}
                    </td>
                    <td className="px-6 py-4 font-medium text-sm">
                      ${tx?.amount?.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {formatDate(tx?.createdAt)}
                    </td>
                    <td className="px-6 py-4 font-medium text-sm">
                      <span
                        className={
                          tx.paymentMethod === "cash"
                            ? "text-success"
                            : "text-primary"
                        }
                      >
                        {tx.paymentMethod}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden space-y-4">
        {filteredTransactions.map((tx) => (
          <div
            key={tx._id}
            className="bg-card border border-border rounded-lg p-4"
          >
            <div className="flex justify-between mb-3">
              <div>
                <p className="text-sm font-mono text-foreground mb-1">
                  {tx._id}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatDate(tx.createdAt)}
                </p>
              </div>
              <div>
                <span
                  className={`inline-flex items-center space-x-1 px-2 py-1 text-xs font-medium rounded-full ${
                    tx.paymentMethod === "cash"
                      ? "bg-success/10 text-success"
                      : "bg-primary/10 text-primary"
                  }`}
                >
                  <Icon
                    name={tx.paymentMethod === "cash" ? "DollarSign" : "CreditCard"}
                    size={12}
                  />
                  <span>
                    {tx.paymentMethod.charAt(0).toUpperCase() +
                      tx.paymentMethod.slice(1)}
                  </span>
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Employee:</span>
                <span className="text-sm font-medium text-foreground">
                  {tx.employeeId?.name}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Products:</span>
                <span className="text-sm text-foreground">
                  {tx?.services
                    ?.map((s) => `${s.name} (Qty: ${s.quantity})`)
                    .join(", ")}
                </span>
              </div>
              <div className="flex justify-between pt-2 border-t border-border">
                <span className="text-sm font-medium text-muted-foreground">
                  Amount:
                </span>
                <span className="text-lg font-bold text-foreground">
                  ${tx?.amount?.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* No data message */}
      {filteredTransactions.length === 0 && (
        <div className="text-center py-12">
          <Icon
            name="Receipt"
            size={48}
            className="mx-auto text-muted-foreground mb-4"
          />
          <h3 className="text-lg font-medium text-foreground mb-2">
            No transactions found
          </h3>
          <p className="text-muted-foreground">
            {searchTerm || dateFilter !== "all" || statusFilter !== "all" || productFilter
              ? "Try adjusting your filters"
              : "Transactions will appear here once employees start making purchases"}
          </p>
        </div>
      )}
    </div>
  );
};

export default BillingHistory;
